<script setup lang="ts">
import { ref, computed, watch, reactive, onMounted } from "vue"
import { useRoute } from 'vue-router'
import Button from '~/components/ui/button/Button.vue'
import Icon from '~/components/ui/icon/Icon.vue'
import { IconCalendar, IconCalendarCheck, IconCaretRight, IconChecks, IconInfo, IconSealCheckFill } from '~/components/ui/icon/icons'
import PopupCommon from '~/components/ui/popup/PopupCommon.vue'
import enquiryApi from '~/composables/api/enquiryApi'
import { addDays } from '~/helper'
import { useTripPlanStore } from '~/store/pinia/tripPlan'

const props = defineProps({
  designer: {
    type: Object,
    required: true,
  },
})

const ONE_DAY_NUMBER = 24 * 60 * 60 * 1000
const MAX_DATE_NUMBER = 180 * ONE_DAY_NUMBER

const route = useRoute()
const tripPlanStore = useTripPlanStore()

const durationContext = ref<null | { message: string; icon: any; color: string }>(null)
const modalActive = ref(false)
const loading = ref(false)
const selectEndDate = ref(false)

const popover = ref<any>({
  visibility: 'focus',
  autoHide: true,
  showDelay: 100,
  hideDelay: 90,
})
const attrs = ref([
  {
    dates: new Date(),
  },
])
const datePickerStartRef = ref<any>(null)
const datePickerEndRef = ref<any>(null)

const dataFrom = reactive({
  startDate: null as Date | null,
  endDate: null as Date | null,
})
const dataTrip = computed(() => tripPlanStore.dataTrip)
const tripDuration = computed(() => {
  const length = tripPlanStore.dataEnquiry?.sketchInfo
  return dataTrip.value?.itinerary?.length || parseInt(length?.trip_length) || parseInt(length?.trip_length_range)
})
const isFilledDate = computed<boolean>(() => !!dataFrom.startDate && !!dataFrom.endDate)
const startDate = computed(() => dataFrom.startDate)
const endDate = computed(() => dataFrom.endDate)
const subtext = computed(() => {
  if (dataFrom.startDate && dataFrom.endDate) {
    const nowStart = new Date(dataFrom.startDate)
    const nowEnd = new Date(dataFrom.endDate)
    const newDuration = Math.ceil((nowEnd.getTime() - nowStart.getTime() + 1) / ONE_DAY_NUMBER)
    if (newDuration === tripDuration.value) {
      return `Perfect! We'll let ${props?.designer?.name} know about this update. ${props?.designer?.name} will let you know if there are any changes to the availability or options for a better deal.`
    }
    return `Thanks for letting us know you want to ${
      newDuration > tripDuration.value ? 'extend' : 'shorten'
    } the trip. Please message us the details in the chat.`
  }
  return 'Confirm your travel date so the local designer can confirm the availability and advise you on the best deal.'
})

watch(startDate, (date) => {
  if (date) {
    setTimeout(() => {
      dataFrom.endDate = addDays(date, tripDuration.value - 1)
    }, 100)
    durationContext.value = {
      color: 'brand-950',
      icon: 'checks',
      message: 'Auto-Selected based on your duration',
    }
  } else {
    dataFrom.endDate = null
    durationContext.value = null
  }
})

watch(endDate, (date) => {
  if (selectEndDate.value) {
    if (date && dataFrom.startDate) {
      const nowStart = new Date(dataFrom.startDate)
      const nowEnd = new Date(date)
      const newDuration = Math.ceil(((nowEnd.getTime() - nowStart.getTime()) + 1) / ONE_DAY_NUMBER)
      if (newDuration === tripDuration.value) {
        durationContext.value = {
          color: 'brand-950',
          icon: 'checks',
          message: 'Selected based on your duration',
        }
      } else {
        durationContext.value = {
          color: 'gray-950',
          icon: 'info',
          message: `New Duration: ${newDuration} day${newDuration > 1 ? 's' : ''} (was: ${tripDuration.value} day${tripDuration.value > 1 ? 's' : ''})`,
        }
      }
    } else {
      durationContext.value = null
    }
  }
})

function closeModal() {
  modalActive.value = false
  resetData()
}
function toggleModalConfirm() {
  if (modalActive.value === false) {
  }
  modalActive.value = !modalActive.value
}
async function confirmDate() {
  try {
    loading.value = true
    const res = await enquiryApi.updateEnquiryDuration(dataTrip.value?.enquiry_id, {
      start_date: dataFrom.startDate?.toDateString() || null,
      end_date: dataFrom.endDate?.toDateString() || null,
      trip_id: dataTrip.value?.id || undefined,
      designer_id: dataTrip.value?.designer?.id || undefined,
    })
    if (res.data.status === 200) {
      closeModal()
    }
    console.log(res.data)
  } catch (e) {
    console.log(e)
  } finally {
    loading.value = false
  }
}
function resetData() {
  dataFrom.startDate = null
  dataFrom.endDate = null
  durationContext.value = null
}

function closePopover() {
  setTimeout(() => {
    if (datePickerStartRef.value && datePickerEndRef.value) {
      datePickerStartRef.value.hidePopover()
      datePickerEndRef.value.hidePopover()
    }
  }, 100)
}

function handleStartDayClick() {
  selectEndDate.value = false
  closePopover()
}
function handleEndDayClick() {
  selectEndDate.value = true
  closePopover()
}
defineExpose({
  toggleModalConfirm,
  closeModal,
})
onMounted(() => {
  if (route.query.download && route.query.download === '1') {
    modalActive.value = true
  }
})
</script>

<template>
  <PopupCommon modalTitle="Confirm Your Travel Date" :modalActive="modalActive" @update:close="closeModal" contentCustomClass="pa-16" maxWidth="560">
    <template #content>
      <div class="grid grid-gap-16">
        <div class="flex mb-flex-column grid-gap-16">
          <div class="wp-100">
            <label for="startDate" class="text-md-sb mb-4">Start date</label>
            <VDatePicker
              ref="datePickerStartRef"
              v-model="dataFrom.startDate"
              :min-date="new Date(Date.now())"
              :masks="{ input: 'DD MMM YYYY', weekdays: 'WW' }"
              :attributes="attrs"
              :popover="popover"
              @dayclick="handleStartDayClick"
            >
              <template #default="{ inputValue, inputEvents, showPopover }">
                <div class="relative" @click="showPopover()">
                  <input
                    id="startDate"
                    :value="inputValue"
                    v-on="inputEvents"
                    readonly
                    placeholder="MMM DD YYYY"
                    class="dj-input h-44 px-12 py-10 br-6 bd-1 bd-gray-medium fs-14 lh-17 wp-100 outline-0"
                  />
                  <div class="absolute right-12 top-10">
                    <Icon :size="24">
                      <IconCalendar />
                    </Icon>
                  </div>
                </div>
              </template>
            </VDatePicker>
          </div>
          <div class="wp-100">
            <label for="endDate" class="text-md-sb mb-4">End date</label>
            <VDatePicker
              ref="datePickerEndRef"
              v-model="dataFrom.endDate"
              :min-date="new Date(dataFrom.startDate || Date.now())"
              :max-date="dataFrom.startDate ? new Date(dataFrom.startDate.getTime() + MAX_DATE_NUMBER) : undefined"
              :masks="{ input: 'DD MMM YYYY', weekdays: 'WW' }"
              :attributes="attrs"
              :popover="popover"
              :style="{
                cursor: dataFrom.startDate ? 'pointer' : 'not-allowed',
              }"
              @dayclick="handleEndDayClick"
            >
              <template #default="{ inputValue, inputEvents, showPopover }">
                <div
                  class="relative"
                  :style="{
                    'pointer-events': dataFrom.startDate ? 'auto' : 'none',
                  }"
                  @click="showPopover()"
                >
                  <input
                    id="endDate"
                    :value="inputValue"
                    v-on="inputEvents"
                    readonly
                    placeholder="MMM DD YYYY"
                    :disabled="!dataFrom.startDate"
                    class="dj-input h-44 px-12 py-10 br-6 bd-1 bd-gray-medium fs-14 lh-17 wp-100 outline-0"
                  />
                  <div class="absolute right-12 top-10">
                    <Icon :size="24">
                      <IconCalendarCheck />
                    </Icon>
                  </div>
                </div>
              </template>
            </VDatePicker>
            <div v-if="durationContext" :class="['mt-4 flex items-center', durationContext.color]">
              <Icon>
                <component :is="durationContext.icon === 'checks' ? IconChecks : IconInfo" />
              </Icon>
              <span class="ml-2 text-sm-rg">
                {{ durationContext.message }}
              </span>
            </div>
          </div>
        </div>
        <div class="flex">
          <Icon class="mt-4" :size="20" color="#197660">
            <IconSealCheckFill />
          </Icon>
          <span class="ml-8 text-md-md">{{ subtext }}</span>
        </div>
        <Button id="button-bookable-trip-plan-confirm-travel-date-popup" :loading="loading" class="wp-100" :disabled="!isFilledDate" :suffix-icon="IconCaretRight" @click.prevent="confirmDate()">
          Confirm Your Travel Date
        </Button>
      </div>
    </template>
  </PopupCommon>
</template>

<style lang="scss" scoped></style>
